import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl/dist';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl/dist';
import { GIButton } from 'app-components';
import {
    fetchCurrentUserViewRef,
    fetchDmiVerificationsData,
    loadUploadedVerificationDocuments,
    uploadDocumentUsingQRCode,
    uploadVerificationDocuments,
} from '../../../actions/dmi/dmiActionCreators';
import Button from '../../../components/common/button/Button';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import NumericInput from '../../../components/common/form/NumericInput';
import { configs } from '../../../constants/configs/configs';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';
import { FORMATTED_DATES } from '../../../constants/individualPortal/dashboard/dashboardModelTypes';
import { VERIFICATION_TYPE } from '../constants';
import withModalWrapper from '../../../containers/common/WithModalWrapper';
import { useAppDispatch, useAppSelector, useModal } from '../../../hooks';
import Form from '../../../lib/wrappers/Form';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import { environmentSelector } from '../../../selectors/commonSelectors';
import { dateMethods, DATE_FORMAT } from '../../../utils/dates/dateMethods';
import { parseDateWithFormat } from '../../../utils/dates/date.utils';
import { formatCurrency } from '../../../utils/tools/formatCurrenyToInteger';
import {
    StyledAmountInput,
    StyledButton,
    StyledContainer,
    StyledDisabledInput,
    StyledDocumentButton,
    StyledHeaderContainer,
    StyledAllowedFiles,
    StyledInputUpload,
    StyledLabel,
    StyledLabelName,
    StyledSubHeaderLabel,
    StyledUploadContainer,
    StyledUploadDocument,
    StyledButtonContainer,
    StyledButtonDMI,
    StyledTextPin,
    StyledAllowedFilesError,
    StyledSelfEmploymentMessage,
} from '../utils/StyledComponents';
import { VERIFICATION } from '../constants';
import verificationsHelper from '../helpers/verificationsHelper';
import { modalConfig } from '../modals/modalConfig';
import { IVerificationsDocumentUploadPayload } from '../types/dmiTypes';
import VerificationsIncomeDocumentsTable from './VerificationsIncomeDocumentsTable';
import VerificationsTable from './VerificationsTable';

const VerificationsDocumentUpload = ({
    name,
    verificationType,
    ropDate,
    documentTypeList,
    applicantId,
    documentCategory,
    documentCategoryDetailList,
    isDocumentCategoryVerified,
    exchangeName,
    sourceEn,
    sourceEs,
}: IVerificationsDocumentUploadPayload) => {
    const dispatch = useAppDispatch();
    const environment = useAppSelector(environmentSelector);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [currentDocument, setCurrentDocument] = useState<{ documentType: string }>({ documentType: '' });
    const { documentType } = currentDocument;
    const [documentAmount, setDocumentAmount] = useState(0);
    const [incomeDocumentData, setIncomeDocumentData] = useState<
        { documentType: string; amount: number; documentName: string; selectedFile }[]
    >([]);
    const fileInputRef = useRef(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const history = useHistory();
    const { viewRef } = useAppSelector(state => state.dmi);
    const householdIncome = useAppSelector(state => state.dmi.householdIncome);
    const intl = useIntl();
    const { language } = useAppSelector(state => state.common.environment);
    const {
        user: { userId },
        stateCode,
        isDocumentUploadWithQRCodeEnable,
    } = useAppSelector(state => state.common.environment);
    const {
        data: { applicationID: applicantionID, caseId: case_number },
    } = useAppSelector(state => state.dmi.verificationsData);
    const { openModal } = useModal();
    const {
        getDropdownOptions,
        getFileNameWithAmount,
        isAllowedStateForQRCode,
        isNonIncomeDMI,
        isSelfEmploymentDocumentValid,
    } = verificationsHelper();
    const isIncomeVerificationType = verificationType === VERIFICATION.INCOME;
    const key = `${name}-${applicantId}-${verificationType}`;
    const showQRCodeModal =
        isNonIncomeDMI(verificationType) &&
        isAllowedStateForQRCode(stateCode) &&
        !isDocumentCategoryVerified &&
        isDocumentUploadWithQRCodeEnable;
    const isVerificationTypeDeathOrNonEsi =
        verificationType === VERIFICATION_TYPE.DEATH ||
        verificationType === VERIFICATION_TYPE.NON_ESI_MINIMUM_ESSENTIAL_COVERAGE;
    const isVerificationTypeDeath = verificationType === VERIFICATION_TYPE.DEATH;
    const isVerificationTypeNonEsi = verificationType === VERIFICATION_TYPE.NON_ESI_MINIMUM_ESSENTIAL_COVERAGE;

    useEffect(() => {
        if (!viewRef) {
            dispatch(fetchCurrentUserViewRef(environment));
        }
    }, [viewRef]);

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    useEffect(() => {
        setFileError(null);
    }, [intl]);

    const onFieldChange = (newData: { documentType: string }): void => {
        if (newData.documentType.startsWith(VERIFICATION.STARTS_WITH)) {
            openModal('documentTypeModal');
            setCurrentDocument({ documentType: '' });
            return;
        }
        setCurrentDocument({ ...newData });
    };

    const onAmountFieldChange = value => {
        setDocumentAmount(value);
    };

    const resetField = () => {
        setSelectedFile(null);
        setFileName('');
        setCurrentDocument({ documentType: '' });
        setDocumentAmount(0);
        setFileError(null);
    };

    const isButtonDisabled = useMemo(() => {
        return !documentType || !selectedFile;
    }, [documentType, selectedFile]);

    const handleDocumentDelete = index => {
        setIncomeDocumentData(prevDocuments => prevDocuments.filter((_, i) => i !== index));
    };

    const handleAddDocument = () => {
        if (!documentType || !fileName || !documentAmount || !selectedFile) {
            return;
        }
        setIncomeDocumentData(prev => [
            ...prev,
            {
                documentType,
                amount: documentAmount,
                documentName: getFileNameWithAmount(fileName, documentAmount),
                selectedFile,
            },
        ]);
        resetField();
    };

    const handleFileChange = event => {
        const file = event.target.files[0];
        if (!file) {
            event.target.value = null;
            return;
        }

        const fileSizeInMB = file.size / (1024 * 1024);
        const isFileSizeValid = fileSizeInMB <= VERIFICATION.MAX_FILE_SIZE_IN_MB;
        const isFileTypeValid = VERIFICATION.ALLOWED_FILE_TYPES.includes(file.type);

        if (!isFileSizeValid) {
            const errorMessage = intl.formatMessage({ id: 'dmi.applicantVerifications.pleaseUploadFileUnderFive' });
            setFileError(errorMessage);
        } else if (!isFileTypeValid) {
            const errorMessage = intl.formatMessage({ id: 'ticket.invalid.file.type' });
            setFileError(errorMessage);
        } else {
            setSelectedFile(file);
            setFileName(file.name);
            setFileError(null);
        }

        if (!isFileSizeValid || !isFileTypeValid) {
            event.target.value = null;
            setSelectedFile(null);
            setFileName('');
        }
    };

    const handleSubmit = event => {
        event.preventDefault();
        const formData = new FormData();
        formData.append('case_number', case_number);
        formData.append('applicantionID', applicantionID);
        formData.append('csrftoken', window.csrf_token);
        formData.append('viewRef', viewRef as any);
        formData.append('documentCategory', documentCategory);
        formData.append('applicantID', applicantId);
        formData.append('houseHoldIncome', householdIncome);

        if (isIncomeVerificationType && incomeDocumentData.length > 0) {
            incomeDocumentData.forEach(data => {
                formData.append('files[]', data?.selectedFile);
                formData.append('documentTypes', data?.documentType);
            });
        } else {
            if (selectedFile) {
                formData.append('files[]', selectedFile as any);
            }
            formData.append('documentTypes', documentType);
        }

        dispatch(
            uploadVerificationDocuments(configs.endpoints.DMI_UPLOAD_DOCUMENT_ENDPOINT, formData, data => {
                if (data.length) {
                    dispatch(
                        fetchDmiVerificationsData(
                            configs.endpoints.DMI_GET_DMI_VERIFICATIONS.processReplacements({
                                caseNumber: case_number,
                                language,
                            }),
                            environment,
                        ),
                    );
                }
            }),
        );
        // reset the fields after submit
        resetField();
        setIncomeDocumentData([]);
    };

    const handleQRCodeModal = () => {
        const params = {
            applicantId: applicantId,
            documentType: documentType,
            documentCategory: documentCategory,
            userId: userId,
        };
        dispatch(
            uploadDocumentUsingQRCode(configs.endpoints.DMI_UPLOAD_DOCUMENT_QR_CODE_ENDPOINT, params, data => {
                if (data) {
                    openModal('qrCodeModal', {
                        qrCode: data?.qrCode,
                        pin: data?.pin,
                        websiteName: data?.websiteName,
                        applicantId: applicantId,
                        documentCategory: documentCategory,
                        documentType: documentType,
                        userId: userId,
                    });
                }
            }),
        );
    };

    const onLoadDocumentHandler = docPath => {
        dispatch(
            loadUploadedVerificationDocuments(
                configs.endpoints.DMI_LOAD_DOCUMENT_ENDPOINT,
                {
                    applicantId: applicantId,
                    documentCategory: documentCategory,
                },
                data => {},
            ),
        );
        dispatch(
            fetchDmiVerificationsData(
                configs.endpoints.DMI_GET_DMI_VERIFICATIONS.processReplacements({ caseNumber: case_number, language }),
                environment,
            ),
        );
    };

    return (
        <StyledUploadContainer>
            <ConditionalRendering isTrue={isIncomeVerificationType && documentCategoryDetailList.length === 0}>
                <StyledHeaderContainer>
                    <StyledSubHeaderLabel data-testid={`${key}-header`}>
                        <FormattedMessage
                            id="dmi.applicantVerifications.incomeNotVerifiedBeforeUpload"
                            values={{
                                income: formatCurrency(householdIncome).split('.')[0],
                                strong: function makeTextStrong(chunks) {
                                    return <strong>{chunks}</strong>;
                                },
                                u: function makeTextUnderline(chunks) {
                                    return <u>{chunks}</u>;
                                },
                                anchor: function makeAnchorTag(chunks) {
                                    return (
                                        <strong>
                                            <a
                                                className="anchor-on-click"
                                                onClick={() => history.push(configs.routes.INDPORTAL)}
                                            >
                                                <u>{chunks}</u>
                                            </a>
                                        </strong>
                                    );
                                },
                                lineBreak: <br />,
                            }}
                        />
                    </StyledSubHeaderLabel>
                </StyledHeaderContainer>
            </ConditionalRendering>
            <ConditionalRendering isTrue={isIncomeVerificationType && documentCategoryDetailList.length > 0}>
                <StyledHeaderContainer>
                    <StyledSubHeaderLabel data-testid={`${key}-header`}>
                        <FormattedMessage
                            id="dmi.applicantVerifications.incomeNotVerifiedAfterUpload"
                            values={{
                                name,
                                verificationType,
                                income: formatCurrency(householdIncome).split('.')[0],
                                strong: function makeTextStrong(chunks) {
                                    return <strong>{chunks}</strong>;
                                },
                                anchor: function makeAnchorTag(chunks) {
                                    return (
                                        <strong>
                                            <a
                                                className="anchor-on-click"
                                                onClick={() => history.push(configs.routes.INDPORTAL)}
                                            >
                                                {chunks}
                                            </a>
                                        </strong>
                                    );
                                },
                                lineBreak: <br />,
                            }}
                        />
                    </StyledSubHeaderLabel>
                </StyledHeaderContainer>
            </ConditionalRendering>
            <ConditionalRendering isTrue={!isIncomeVerificationType}>
                <StyledHeaderContainer>
                    <StyledSubHeaderLabel data-testid={`${key}-header`}>
                        <ConditionalRendering isTrue={!isVerificationTypeDeathOrNonEsi}>
                            <FormattedMessage
                                id="dmi.applicantVerifications.notVerified"
                                values={{
                                    name,
                                    verificationType,
                                    ropDate: dateMethods.getFormattedDate(FORMATTED_DATES.commonDate, parseDateWithFormat(ropDate, DATE_FORMAT)),
                                    anchor: function makeAnchorTag(chunks) {
                                        return (
                                            <strong>
                                                <a
                                                    className="anchor-on-click"
                                                    onClick={() => history.push(configs.routes.INDPORTAL)}
                                                >
                                                    {chunks}
                                                </a>
                                            </strong>
                                        );
                                    },
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isVerificationTypeDeath}>
                            <FormattedMessage
                                id="dmi.applicantVerifications.death"
                                values={{
                                    name,
                                    verificationType,
                                    ropDate: dateMethods.getFormattedDate(FORMATTED_DATES.commonDate, parseDateWithFormat(ropDate, DATE_FORMAT)),
                                    para: function makeParaTag(chunks) {
                                        return <p>{chunks}</p>;
                                    },
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isVerificationTypeNonEsi}>
                            <FormattedMessage
                                id="dmi.applicantVerifications.nonesi"
                                values={{
                                    name,
                                    verificationType,
                                    exchangeName,
                                    sourceEn,
                                    sourceEs,
                                    ropDate: dateMethods.getFormattedDate(FORMATTED_DATES.commonDate, parseDateWithFormat(ropDate, DATE_FORMAT)),
                                    para: function makeParaTag(chunks) {
                                        return <p>{chunks}</p>;
                                    },
                                }}
                            />
                        </ConditionalRendering>
                    </StyledSubHeaderLabel>
                </StyledHeaderContainer>
            </ConditionalRendering>

            <ConditionalRendering isTrue={showQRCodeModal}>
                <StyledHeaderContainer>
                    <StyledSubHeaderLabel data-testid={`${key}-header`}>
                        <FormattedMessage
                            id="dmi.applicantVerifications.help.document.load"
                            values={{
                                anchor: function makeAnchorTag(chunks) {
                                    return (
                                        <strong>
                                            <a className="anchor-on-click" onClick={onLoadDocumentHandler}>
                                                {chunks}
                                            </a>
                                        </strong>
                                    );
                                },
                            }}
                        />
                    </StyledSubHeaderLabel>
                </StyledHeaderContainer>
            </ConditionalRendering>
            <ConditionalRendering isTrue={documentCategoryDetailList.length > 0}>
                <VerificationsTable
                    key={`verificationsTable-${key}`}
                    documentCategoryDetailList={documentCategoryDetailList}
                    documentCategory={documentCategory}
                    applicantId={applicantId}
                />
            </ConditionalRendering>
            <StyledContainer data-testid={`dmiForm-${key}`}>
                <Form {...{ formName: `dmiForm-${key}` }}>
                    <UniversalInput
                        label={{
                            text: <FormattedMessage id="ridp.manualverification.document.input.placeholder" />,
                            ulClass: ['margin-b-20'],
                        }}
                        inputData={{
                            id: `documentType-${key}`,
                            isInputRequired: true,
                            type: INPUT_TYPES.DROPDOWN,
                            name: 'documentType',
                            value: documentType,
                            options: getDropdownOptions(documentTypeList, language),
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: currentDocument,
                            fields: [],
                        }}
                        inputActions={{
                            onFieldChange: onFieldChange,
                        }}
                    />
                    <StyledUploadDocument className="grid-row">
                        <StyledLabelName className="required-true grid-col-4" htmlFor={`fileUpload-${key}`}>
                            <FormattedMessage id="dmi.applicantVerifications.documentlabel" />
                        </StyledLabelName>
                        <StyledButtonContainer
                            isIncomeVerificationType={isIncomeVerificationType}
                            className="grid-col-8"
                        >
                            <StyledButtonDMI>
                                <StyledButtonDMI htmlFor={`fileUpload-${key}`} documentType={documentType}>
                                    <Button
                                        id={`dmiAddDocument-${key}`}
                                        classes={['dmiActionButton']}
                                        data-testid={`fileUpload-button-${key}`}
                                        type="button"
                                        btnText={
                                            isIncomeVerificationType
                                                ? 'ridp.manualverification.fileplaceholder'
                                                : 'dmi.applicantVerifications.fileplaceholder'
                                        }
                                        disabled={!documentType}
                                        handleClick={handleButtonClick}
                                    />
                                </StyledButtonDMI>
                                <StyledInputUpload
                                    type="file"
                                    id={`fileUpload-${key}`}
                                    data-testid={`fileUpload-${key}`}
                                    onChange={handleFileChange}
                                    className="file-input"
                                    accept={VERIFICATION.ALLOWED_EXT}
                                    disabled={!documentType}
                                    ref={fileInputRef}
                                />
                                <ConditionalRendering isTrue={isIncomeVerificationType && !fileError}>
                                    <StyledAllowedFiles isNonIncomeDocument={isNonIncomeDMI(verificationType)}>
                                        <FormattedMessage id="ticket.invalid.file.type" />
                                    </StyledAllowedFiles>
                                </ConditionalRendering>
                            </StyledButtonDMI>
                            <ConditionalRendering isTrue={showQRCodeModal}>
                                <StyledTextPin
                                    className="grid-col-1"
                                    isOrCondition={true}
                                    isNonIncomeDocument={isNonIncomeDMI(verificationType)}
                                >
                                    <FormattedMessage id="anonymous.pre_eligibility.pre_screen.or" />
                                </StyledTextPin>
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={showQRCodeModal}>
                                <StyledButtonDMI documentType={documentType}>
                                    <Button
                                        id={`dmiAddDocument-${key}`}
                                        data-testid={`qrCode-${key}`}
                                        name="qrCode"
                                        classes={['dmiActionButton']}
                                        type="button"
                                        btnText="dmi.applicantVerifications.qrCode"
                                        handleClick={handleQRCodeModal}
                                        disabled={!documentType}
                                    />
                                </StyledButtonDMI>
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={!!fileName && !!documentType}>
                                <StyledDisabledInput type="text" title={fileName} disabled placeholder={fileName} />
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={!!fileError}>
                                <StyledAllowedFilesError>{fileError}</StyledAllowedFilesError>
                            </ConditionalRendering>
                        </StyledButtonContainer>
                    </StyledUploadDocument>
                    <ConditionalRendering isTrue={isIncomeVerificationType}>
                        <StyledAmountInput>
                            <NumericInput
                                label={{
                                    labelText: 'dmi.applicantVerifications.incomeAmountLabel',
                                    labelClasses: ['grid-col-4'],
                                    wrapperClasses: ['flex-column', 'grid-row', 'flex-sm-row'],
                                    isRequired: true,
                                }}
                                input={{
                                    id: `amountInput-${key}`,
                                    inputClasses: ['grid-col-6 amount-input-field'],
                                    name: 'amount',
                                    type: 'number',
                                    value: documentAmount,
                                    min: VERIFICATION.AMOUNT_INPUT_MIN_VALUE,
                                    max: VERIFICATION.AMOUNT_INPUT_MAX_VALUE,
                                    isInputRequired: true,
                                }}
                                actions={{
                                    handleFieldChange: onAmountFieldChange,
                                }}
                            >
                                <StyledDocumentButton className="grid-col-2">
                                    <GIButton
                                        type="button"
                                        id={`dmiAddDocument-${key}`}
                                        labelId="dmi.applicantVerifications.incomeAddDocumentLabel"
                                        variant="secondary"
                                        onClickHandler={handleAddDocument}
                                        isDisabled={isButtonDisabled || !documentAmount}
                                    />
                                </StyledDocumentButton>
                            </NumericInput>
                            <ConditionalRendering isTrue={isSelfEmploymentDocumentValid(stateCode, documentType)}>
                                <StyledSelfEmploymentMessage>
                                    <StyledSelfEmploymentMessage className="grid-col-4"></StyledSelfEmploymentMessage>
                                    <StyledSelfEmploymentMessage className="grid-col-8">
                                        <FormattedMessage id="dmi.applicantVerifications.negativeAmountMessage" />
                                    </StyledSelfEmploymentMessage>
                                </StyledSelfEmploymentMessage>
                            </ConditionalRendering>
                        </StyledAmountInput>
                        <ConditionalRendering isTrue={incomeDocumentData.length > 0}>
                            <VerificationsIncomeDocumentsTable
                                key={`incomeDocumentsTable-${key}`}
                                id={`incomeDocumentsTable-${key}`}
                                incomeDocumentData={incomeDocumentData}
                                householdIncome={householdIncome}
                                deleteHandler={handleDocumentDelete}
                                documentCategoryDetailList={documentCategoryDetailList}
                            />
                        </ConditionalRendering>
                    </ConditionalRendering>
                    <StyledButton isIncomeVerificationType={isIncomeVerificationType}>
                        <GIButton
                            type="submit"
                            id={`dmiVerificationsSubmit-${key}`}
                            labelId="button.submit"
                            variant="primary"
                            onClickHandler={e => handleSubmit(e)}
                            isDisabled={isIncomeVerificationType ? !incomeDocumentData.length : isButtonDisabled}
                        />
                        <ConditionalRendering isTrue={isIncomeVerificationType}>
                            <GIButton
                                id={`cancelButton-${key}`}
                                onClickHandler={e => {
                                    e?.preventDefault();
                                    resetField();
                                    setIncomeDocumentData([]);
                                }}
                                variant="primary"
                                labelId="button.cancel"
                            />
                        </ConditionalRendering>
                    </StyledButton>
                </Form>
            </StyledContainer>
        </StyledUploadContainer>
    );
};

export default withModalWrapper(VerificationsDocumentUpload, modalConfig);
